
import React from 'react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Clock, TrendingUp, Users, Calendar } from 'lucide-react';

const UrgencySection = () => {
  const { isMobile } = useBreakpoint();

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-[#FEE6D8]/30 via-[#FEE6D8]/20 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/6 w-80 h-80 bg-[#FEE6D8]/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/6 w-96 h-96 bg-[#003a70]/10 rounded-full blur-3xl"></div>
        <div className="absolute top-2/3 left-1/2 w-64 h-64 bg-gradient-to-r from-[#FEE6D8]/30 to-[#003a70]/20 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-5xl">
        <div className="space-y-12">
          
          {/* Section Header */}
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#003a70]/20">
              <Clock className="text-[#003a70]" size={24} />
              <span className="text-[#003a70] font-semibold text-lg">Time-Sensitive Opportunity</span>
            </div>
            
            <h2 className={cn(
              "font-bold text-gray-900 leading-tight",
              isMobile ? "text-3xl" : "text-4xl lg:text-5xl"
            )}>
              Here's Why You Need to{" "}
              <span className="text-[#003a70]">
                Act Now
              </span>
            </h2>
            
            <div className="w-24 h-1 bg-gradient-to-r from-[#003a70] via-[#003a70]/80 to-[#FEE6D8] mx-auto rounded-full shadow-sm"></div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            
            {/* First Point - Market Competition */}
            <div className="bg-white p-6 md:p-8 rounded-xl border-l-4 border-[#003a70] shadow-lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-[#FEE6D8] rounded-full">
                  <TrendingUp className="text-[#003a70]" size={24} />
                </div>
                <div>
                  <p className={cn(
                    "text-gray-800 leading-relaxed",
                    isMobile ? "text-lg" : "text-xl"
                  )}>
                    Right now, you're one of the <strong className="text-[#003a70]">first to know</strong> about this approach. But as more consultants discover how easy this is, the market will become more competitive.
                  </p>
                </div>
              </div>
            </div>

            {/* Second Point - Summer Networking */}
            <div className="bg-white p-6 md:p-8 rounded-xl border-l-4 border-[#003a70]/70 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-[#FEE6D8]/80 rounded-full">
                  <Calendar className="text-[#003a70]" size={24} />
                </div>
                <div>
                  <p className={cn(
                    "text-gray-800 leading-relaxed",
                    isMobile ? "text-lg" : "text-xl"
                  )}>
                    <strong className="text-[#003a70]">Summer networking season is in full swing.</strong> Every day you wait is another networking event where you could have been demonstrating this technology and landing recurring clients.
                  </p>
                </div>
              </div>
            </div>

            {/* Visual emphasis box */}
            <div className="bg-gradient-to-br from-[#003a70] to-[#003a70]/90 p-8 md:p-10 rounded-2xl text-white shadow-xl relative overflow-hidden">
              <div className="absolute top-4 right-4 w-16 h-16 bg-white/20 rounded-full blur-xl"></div>
              <div className="absolute bottom-4 left-4 w-12 h-12 bg-[#FEE6D8]/20 rounded-full blur-lg"></div>
              
              <div className="relative text-center space-y-4">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Users className="text-white" size={32} />
                  <Clock className="text-white" size={32} />
                </div>
                
                <p className={cn(
                  "font-bold leading-tight",
                  isMobile ? "text-xl" : "text-2xl lg:text-3xl"
                )}>
                  While others are still trying to figure out AI...
                </p>
                
                <p className={cn(
                  "font-semibold leading-relaxed",
                  isMobile ? "text-lg" : "text-xl"
                )}>
                  You could be the consultant who's already demonstrating real value and closing deals with this simple, proven approach.
                </p>
              </div>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default UrgencySection;
